// App.js (Chakra UI integrated)
import React, { useState, useRef, useEffect, useCallback } from "react";
import {
  Stage,
  Layer,
  Line,
  Text as KonvaText,
  Path,
  Circle as KonvaCircle,
  Rect,
} from "react-konva";
import {
  Box,
  Button,
  Flex,
  Input,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Text,
  FormLabel,
  Grid,
  GridItem,
  Icon,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  Select,
} from "@chakra-ui/react";
// Import material design icons from react-icons directly
import { BiCalculator, BiDownload, BiPalette, BiRuler, BiText, BiReset } from "react-icons/bi";

const baseScale = 100;

const App = () => {
  const [inputs, setInputs] = useState([
    { sigma3: "1.5", sigma1: "3.7", center: "", radius: "" },
    { sigma3: "2.5", sigma1: "5.2", center: "", radius: "" },
    { sigma3: "3.5", sigma1: "6.6", center: "", radius: "" },
  ]);
  const [extraTop, setExtraTop] = useState(2.0);
  const [extraRight, setExtraRight] = useState(1.0);
  const [circles, setCircles] = useState([]);
  const [linePoints, setLinePoints] = useState(null);
  const [slope, setSlope] = useState(null);
  const [intercept, setIntercept] = useState(null);
  const [bgColor, setBgColor] = useState("#cccccc");
  
  // Add color state for circles and other elements
  const [circleColors, setCircleColors] = useState(["#2b6cb0", "#38a169", "#e53e3c"]);
  const [tangentColor, setTangentColor] = useState("#d53f8c");
  const [axisColor, setAxisColor] = useState("#000000");
  const [axisMarkingsColor, setAxisMarkingsColor] = useState("#000000");
  const [textColor, setTextColor] = useState("#000000");
  
  // Add line thickness state
  const [circleThickness, setCircleThickness] = useState([2, 2, 2]);
  const [tangentThickness, setTangentThickness] = useState(2);
  const [axisThickness, setAxisThickness] = useState(1);
  const [axisMarkingsThickness, setAxisMarkingsThickness] = useState(1);
  
  // Add text styling state - separate for axis labels and slope text
  const [axisTextFontSize, setAxisTextFontSize] = useState(12);
  const [slopeTextFontSize, setSlopeTextFontSize] = useState(16);
  const [axisTextFontFamily, setAxisTextFontFamily] = useState("Arial");
  const [slopeTextFontFamily, setSlopeTextFontFamily] = useState("Arial");
  const [axisTextFontStyle, setAxisTextFontStyle] = useState("normal"); // normal, bold, italic
  const [slopeTextFontStyle, setSlopeTextFontStyle] = useState("bold"); // normal, bold, italic
  
  const stageRef = useRef(null);
  const containerRef = useRef(null);
  const [canvasSize, setCanvasSize] = useState({
    width: 800,
    height: 400,
    appliedScale: 1,
    xMin: -1.2,
    yMin: -1.2,
  });
  const padding = 1.2;

  // Dark mode color values with better contrast
  const cardBg = "gray.800";
  const tableBg = "gray.900";
  const tableHeaderBg = "blue.900";
  const inputReadOnlyBg = "gray.700";
  const borderColor = "blue.700";
  const pageBg = "gray.900";
  const uiTextColor = "blue.200";
  const labelColor = "gray.200";
  const resultsBg = "blue.900";
  
  // Special colors for input fields
  const editableFieldColor = "#3DEAFF"; // Bright cyan for editable fields

  // DO NOT CHANGE ANY OF THESE FUNCTIONS - Keep core logic intact
  const calculateCanvasSize = useCallback((circlesData) => {
    const maxCircleRight = Math.max(...circlesData.map(c => c.center + c.radius));
    const maxCircleUp = Math.max(...circlesData.map(c => c.radius));
    const xMin = -1.2;
    const yMin = -1.2;
    const xMax = maxCircleRight + padding + parseFloat(extraRight || 1.0);
    const yMax = maxCircleUp + padding + parseFloat(extraTop || 2.0);
    const contentWidth = (xMax - xMin) * baseScale;
    const contentHeight = (yMax - yMin) * baseScale;
    
    // Get the available container width, accounting for padding
    const containerWidth = (containerRef.current?.clientWidth || window.innerWidth) - 40; // Subtract padding
    const containerHeight = window.innerHeight - 200;
    
    // Calculate scale factors - ensure the canvas fits within the container
    const scaleX = containerWidth / contentWidth;
    const scaleY = containerHeight / contentHeight;
    const appliedScale = Math.min(scaleX, scaleY, 1);
    
    // Apply the scale to fit everything
    return { 
      width: Math.min(contentWidth * appliedScale, containerWidth), 
      height: contentHeight * appliedScale, 
      appliedScale, 
      xMin, 
      yMin 
    };
  }, [extraRight, extraTop]);

  useEffect(() => {
    if (circles.length) {
      const newSize = calculateCanvasSize(circles);
      setCanvasSize(newSize);
    }
  }, [extraTop, extraRight, circles, calculateCanvasSize]);

  const handleInputChange = (index, field, value) => {
    const newInputs = [...inputs];
    newInputs[index][field] = value;
    setInputs(newInputs);
  };

  const calculateCircles = async () => {
    try {
      const response = await fetch('/api/calculate-circles', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ inputs }),
      });
      if (!response.ok) throw new Error('Failed to calculate');
      const { circles: newCircles } = await response.json();
      const updatedInputs = inputs.map((row, i) => ({ ...row, center: newCircles[i].center, radius: newCircles[i].radius }));
      setInputs(updatedInputs);
      const numericCircles = newCircles.map(c => ({ center: parseFloat(c.center), radius: parseFloat(c.radius) }));
      setCircles(numericCircles);
      const newCanvasSize = calculateCanvasSize(numericCircles);
      setCanvasSize(newCanvasSize);
      const maxSigma1 = Math.max(...inputs.map(i => parseFloat(i.sigma1)));
      const maxRadius = Math.max(...numericCircles.map(c => c.radius));
      const x1 = (0 - newCanvasSize.xMin) * baseScale * newCanvasSize.appliedScale;
      const y1 = newCanvasSize.height - (-newCanvasSize.yMin) * baseScale * newCanvasSize.appliedScale;
      const x2 = (maxSigma1 + padding - newCanvasSize.xMin) * baseScale * newCanvasSize.appliedScale;
      const y2 = y1 - maxRadius * baseScale * newCanvasSize.appliedScale;
      setLinePoints({ x1, y1, x2, y2 });
      setSlope(null);
      setIntercept(null);
    } catch (err) {
      console.error("Circle calculation failed", err);
      setCircles([]);
    }
  };

  const calculateLineEquation = async () => {
    if (!linePoints) return;
    const { x1, y1, x2, y2 } = linePoints;
    try {
      const response = await fetch('/api/calculate-line', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ x1, y1, x2, y2, canvasHeight: canvasSize.height, appliedScale: canvasSize.appliedScale, baseScale, xMin: canvasSize.xMin, yMin: canvasSize.yMin }),
      });
      if (!response.ok) throw new Error('API request failed');
      const data = await response.json();
      setSlope(data.slope);
      setIntercept(data.intercept);
    } catch (err) {
      console.error('API error:', err);
      setSlope('Err');
      setIntercept('Err');
    }
  };

  const handleDrag = (point, pos) => {
    setLinePoints(prev => ({ ...prev, [point]: pos.y }));
  };

  const exportImage = () => {
    if (!stageRef.current) return;
    const stage = stageRef.current;
    const layer = stage.getLayers()[0];
    const background = new window.Konva.Rect({ x: 0, y: 0, width: canvasSize.width, height: canvasSize.height, fill: bgColor });
    layer.add(background);
    background.moveToBottom();
    layer.draw();
    const uri = stage.toDataURL({ mimeType: "image/jpeg", pixelRatio: 2, quality: 1 });
    background.destroy();
    layer.draw();
    const link = document.createElement("a");
    link.download = "mohr_circle_plot.jpg";
    link.href = uri;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const drawAxes = () => {
    const scale = baseScale * canvasSize.appliedScale;
    const x0 = (0 - canvasSize.xMin) * scale;
    const y0 = canvasSize.height - (0 - canvasSize.yMin) * scale;
    const yTicks = [], xTicks = [];
    const yMax = Math.max(...circles.map(c => c.radius), 1) + padding;
    const xMax = Math.max(...circles.map(c => c.center + c.radius), 1) + padding;
    
    // Convert font sizes to numbers for display
    const axisFontSize = axisTextFontSize === "" || axisTextFontSize === "." ? 12 : Number(axisTextFontSize);
    
    for (let i = Math.ceil(canvasSize.yMin); i <= Math.floor(yMax); i++) {
      const y = canvasSize.height - (i - canvasSize.yMin) * scale;
      yTicks.push(
        <Line 
          key={`ytick-${i}`} 
          points={[x0 - 5, y, x0 + 5, y]} 
          stroke={axisMarkingsColor} 
          strokeWidth={axisMarkingsThickness === "" || axisMarkingsThickness === "." ? 1 : Number(axisMarkingsThickness)}
        />, 
        <KonvaText 
          key={`ylabel-${i}`} 
          x={x0 - 30} 
          y={y - 6} 
          text={i.toString()} 
          fontSize={axisFontSize}
          fontFamily={axisTextFontFamily}
          fontStyle={axisTextFontStyle}
          fill={textColor} 
        />
      );
    }
    for (let i = Math.ceil(canvasSize.xMin); i <= Math.floor(xMax); i++) {
      const x = (i - canvasSize.xMin) * scale;
      xTicks.push(
        <Line 
          key={`xtick-${i}`} 
          points={[x, y0 - 5, x, y0 + 5]} 
          stroke={axisMarkingsColor} 
          strokeWidth={axisMarkingsThickness === "" || axisMarkingsThickness === "." ? 1 : Number(axisMarkingsThickness)}
        />, 
        <KonvaText 
          key={`xlabel-${i}`} 
          x={x - 5} 
          y={y0 + 8} 
          text={i.toString()} 
          fontSize={axisFontSize}
          fontFamily={axisTextFontFamily}
          fontStyle={axisTextFontStyle}
          fill={textColor} 
        />
      );
    }
    return (
      <>
        <Line 
          points={[0, y0, canvasSize.width, y0]} 
          stroke={axisColor} 
          strokeWidth={axisThickness === "" || axisThickness === "." ? 1 : Number(axisThickness)} 
        />
        <Line 
          points={[x0, 0, x0, canvasSize.height]} 
          stroke={axisColor} 
          strokeWidth={axisThickness === "" || axisThickness === "." ? 1 : Number(axisThickness)} 
        />
        {xTicks}
        {yTicks}
      </>
    );
  };

  const generateArcPath = (cx, cy, r) => `M ${cx - r} ${cy} A ${r} ${r} 0 0 1 ${cx + r} ${cy}`;

  // Update color handler functions
  const handleCircleColorChange = (index, color) => {
    const newColors = [...circleColors];
    newColors[index] = color;
    setCircleColors(newColors);
  };

  // Add thickness handler functions
  const handleCircleThicknessChange = (index, value) => {
    const newThickness = [...circleThickness];
    newThickness[index] = value;
    setCircleThickness(newThickness);
  };

  return (
    <Box p={{ base: 2, md: 3 }} ref={containerRef} fontFamily="body" maxW="100%" mx="auto" bg={pageBg} minH="100vh">
      <Box mb={3} py={2} borderBottom="1px" borderColor={borderColor}>
        <Text fontSize="xl" fontWeight="bold" color={uiTextColor}>Mohr's Circle Tangent Tool</Text>
      </Box>
      
      <Grid templateColumns={{ base: "1fr", md: "minmax(410px, 450px) 1fr" }} gap={{ base: 3, md: 4 }} maxW="100%" overflow="hidden">
        {/* Left Column - Controls */}
        <GridItem w="100%">
          <Box bg={cardBg} p={3} rounded="md" shadow="lg" mb={3} overflowX="hidden" borderWidth="1px" borderColor={borderColor} className="mohr-control-panel">
            <Text fontSize="sm" fontWeight="semibold" mb={2} color={uiTextColor}>Controls</Text>
            
            {/* Padding controls */}
            <Grid templateColumns="auto 120px 1fr" gap={1} mb={2} alignItems="center">
              <FormLabel fontSize="xs" mb={0} whiteSpace="nowrap" color={labelColor}>Padding Top:</FormLabel>
              <Input 
                type="number" 
                value={extraTop} 
                step="1.0" 
                onChange={e => {
                  // Allow any valid number input including decimals
                  const value = e.target.value;
                  setExtraTop(value);
                  // Clear the tangent line when padding is changed
                  // This forces the user to redraw the semicircles
                  setLinePoints(null);
                  setSlope(null);
                  setIntercept(null);
                }} 
                h="28px"
                bg="gray.700"
                color={editableFieldColor}
                borderWidth="1px"
                borderColor="gray.600"
                width="120px"
                fontSize="xs"
              />
              <Text fontSize="9px" color="gray.400" mb={0} lineHeight="1">
                Increase/ decrease top space to fit tangent if necessary
              </Text>
              
              <FormLabel fontSize="xs" mb={0} whiteSpace="nowrap" color={labelColor}>Padding Right:</FormLabel>
              <Input 
                type="number" 
                value={extraRight} 
                step="1.0" 
                onChange={e => {
                  // Allow any valid number input including decimals
                  const value = e.target.value;
                  setExtraRight(value);
                  // Clear the tangent line when padding is changed
                  // This forces the user to redraw the semicircles
                  setLinePoints(null);
                  setSlope(null);
                  setIntercept(null);
                }} 
                h="28px"
                bg="gray.700"
                color={editableFieldColor}
                borderWidth="1px"
                borderColor="gray.600"
                width="120px"
                fontSize="xs"
              />
              <Text fontSize="9px" color="orange.400" mb={0} fontStyle="italic" fontWeight="medium" lineHeight="1">
                Keep at 1.0 — do not change unless you know what you're doing
              </Text>
            </Grid>

            {/* Color controls - collapsible accordion */}
            <Accordion allowToggle mb={2}>
              <AccordionItem border="1px" borderColor="gray.600" rounded="md">
                <h2>
                  <AccordionButton 
                    py={1} 
                    borderRadius="md"
                    bg="blue.700"
                    _hover={{ bg: "blue.600" }}
                    _expanded={{ bg: "blue.800" }}
                    cursor="pointer"
                    title="Click to expand/collapse"
                    className="accordion-button"
                    fontSize="xs"
                  >
                    <Box flex="1" textAlign="left" fontWeight="medium" color={labelColor}>
                      <Flex align="center">
                        <Icon as={BiPalette} mr={2} boxSize="14px" />
                        Visualization Colors
                      </Flex>
                    </Box>
                    <AccordionIcon />
                  </AccordionButton>
                </h2>
                <AccordionPanel pb={2} pt={1}>
                  <Grid templateColumns="auto 1fr" gap={2} alignItems="center">
                    <FormLabel fontSize="sm" mb={0} whiteSpace="nowrap" color={labelColor}>Background:</FormLabel>
                    <Input 
                      type="color" 
                      value={bgColor} 
                      onChange={e => setBgColor(e.target.value)} 
                      h="32px" 
                      p={1}
                      borderWidth="1px"
                      borderColor="gray.600"
                    />

                    <FormLabel fontSize="sm" mb={0} whiteSpace="nowrap" color={labelColor}>Circle 1:</FormLabel>
                    <Input 
                      type="color" 
                      value={circleColors[0]} 
                      onChange={e => handleCircleColorChange(0, e.target.value)} 
                      h="32px" p={1} borderWidth="1px" borderColor="gray.600"
                    />
                    
                    <FormLabel fontSize="sm" mb={0} whiteSpace="nowrap" color={labelColor}>Circle 2:</FormLabel>
                    <Input 
                      type="color" 
                      value={circleColors[1]} 
                      onChange={e => handleCircleColorChange(1, e.target.value)} 
                      h="32px" p={1} borderWidth="1px" borderColor="gray.600"
                    />
                    
                    <FormLabel fontSize="sm" mb={0} whiteSpace="nowrap" color={labelColor}>Circle 3:</FormLabel>
                    <Input 
                      type="color" 
                      value={circleColors[2]} 
                      onChange={e => handleCircleColorChange(2, e.target.value)} 
                      h="32px" p={1} borderWidth="1px" borderColor="gray.600"
                    />
                    
                    <FormLabel fontSize="sm" mb={0} whiteSpace="nowrap" color={labelColor}>Tangent Line:</FormLabel>
                    <Input 
                      type="color" 
                      value={tangentColor} 
                      onChange={e => setTangentColor(e.target.value)} 
                      h="32px" p={1} borderWidth="1px" borderColor="gray.600"
                    />

                    <FormLabel fontSize="sm" mb={0} whiteSpace="nowrap" color={labelColor}>Axis Lines:</FormLabel>
                    <Input 
                      type="color" 
                      value={axisColor} 
                      onChange={e => setAxisColor(e.target.value)} 
                      h="32px" p={1} borderWidth="1px" borderColor="gray.600"
                    />
                    
                    <FormLabel fontSize="sm" mb={0} whiteSpace="nowrap" color={labelColor}>Axis Markings:</FormLabel>
                    <Input 
                      type="color" 
                      value={axisMarkingsColor} 
                      onChange={e => setAxisMarkingsColor(e.target.value)} 
                      h="32px" p={1} borderWidth="1px" borderColor="gray.600"
                    />
                    
                    <FormLabel fontSize="sm" mb={0} whiteSpace="nowrap" color={labelColor}>Text Labels:</FormLabel>
                    <Input 
                      type="color" 
                      value={textColor} 
                      onChange={e => setTextColor(e.target.value)} 
                      h="32px" p={1} borderWidth="1px" borderColor="gray.600"
                    />
                  </Grid>
                </AccordionPanel>
              </AccordionItem>

              {/* Line Thickness Controls */}
              <AccordionItem border="1px" borderColor="gray.600" rounded="md" mt={1}>
                <h2>
                  <AccordionButton 
                    py={1}
                    borderRadius="md"
                    bg="blue.700"
                    _hover={{ bg: "blue.600" }}
                    _expanded={{ bg: "blue.800" }}
                    cursor="pointer"
                    title="Click to expand/collapse"
                    className="accordion-button"
                    fontSize="xs"
                  >
                    <Box flex="1" textAlign="left" fontWeight="medium" color={labelColor}>
                      <Flex align="center">
                        <Icon as={BiRuler} mr={2} boxSize="14px" />
                        Line Thickness
                      </Flex>
                    </Box>
                    <AccordionIcon />
                  </AccordionButton>
                </h2>
                <AccordionPanel pb={2} pt={1}>
                  <Grid templateColumns="auto 1fr" gap={2} alignItems="center">
                    <FormLabel fontSize="sm" mb={0} whiteSpace="nowrap" color={labelColor}>Circle 1:</FormLabel>
                    <Input 
                      type="number" 
                      value={circleThickness[0]} 
                      onChange={(e) => {
                        const value = e.target.value;
                        // Allow empty, dot, or valid number
                        if (value === "" || value === "." || !isNaN(parseFloat(value))) {
                          handleCircleThicknessChange(0, value);
                        }
                      }}
                      size="sm"
                      h="32px"
                      bg="gray.700"
                      color={editableFieldColor}
                    />
                    
                    <FormLabel fontSize="sm" mb={0} whiteSpace="nowrap" color={labelColor}>Circle 2:</FormLabel>
                    <Input 
                      type="number" 
                      value={circleThickness[1]} 
                      onChange={(e) => {
                        const value = e.target.value;
                        // Allow empty, dot, or valid number
                        if (value === "" || value === "." || !isNaN(parseFloat(value))) {
                          handleCircleThicknessChange(1, value);
                        }
                      }}
                      size="sm"
                      h="32px"
                      bg="gray.700"
                      color={editableFieldColor}
                    />
                    
                    <FormLabel fontSize="sm" mb={0} whiteSpace="nowrap" color={labelColor}>Circle 3:</FormLabel>
                    <Input 
                      type="number" 
                      value={circleThickness[2]} 
                      onChange={(e) => {
                        const value = e.target.value;
                        // Allow empty, dot, or valid number
                        if (value === "" || value === "." || !isNaN(parseFloat(value))) {
                          handleCircleThicknessChange(2, value);
                        }
                      }}
                      size="sm"
                      h="32px"
                      bg="gray.700"
                      color={editableFieldColor}
                    />
                    
                    <FormLabel fontSize="sm" mb={0} whiteSpace="nowrap" color={labelColor}>Tangent Line:</FormLabel>
                    <Input 
                      type="number" 
                      value={tangentThickness} 
                      onChange={(e) => {
                        const value = e.target.value;
                        // Allow empty, dot, or valid number
                        if (value === "" || value === "." || !isNaN(parseFloat(value))) {
                          setTangentThickness(value);
                        }
                      }}
                      size="sm"
                      h="32px"
                      bg="gray.700"
                      color={editableFieldColor}
                    />
                    
                    <FormLabel fontSize="sm" mb={0} whiteSpace="nowrap" color={labelColor}>Axis Lines:</FormLabel>
                    <Input 
                      type="number" 
                      value={axisThickness} 
                      onChange={(e) => {
                        const value = e.target.value;
                        // Allow empty, dot, or valid number
                        if (value === "" || value === "." || !isNaN(parseFloat(value))) {
                          setAxisThickness(value);
                        }
                      }}
                      size="sm"
                      h="32px"
                      bg="gray.700"
                      color={editableFieldColor}
                    />
                    
                    <FormLabel fontSize="sm" mb={0} whiteSpace="nowrap" color={labelColor}>Axis Markings:</FormLabel>
                    <Input 
                      type="number" 
                      value={axisMarkingsThickness} 
                      onChange={(e) => {
                        const value = e.target.value;
                        // Allow empty, dot, or valid number
                        if (value === "" || value === "." || !isNaN(parseFloat(value))) {
                          setAxisMarkingsThickness(value);
                        }
                      }}
                      size="sm"
                      h="32px"
                      bg="gray.700"
                      color={editableFieldColor}
                    />
                  </Grid>
                </AccordionPanel>
              </AccordionItem>

              {/* Text Styling Controls */}
              <AccordionItem border="1px" borderColor="gray.600" rounded="md" mt={1}>
                <h2>
                  <AccordionButton 
                    py={1}
                    borderRadius="md"
                    bg="blue.700"
                    _hover={{ bg: "blue.600" }}
                    _expanded={{ bg: "blue.800" }}
                    cursor="pointer"
                    title="Click to expand/collapse"
                    className="accordion-button"
                    fontSize="xs"
                  >
                    <Box flex="1" textAlign="left" fontWeight="medium" color={labelColor}>
                      <Flex align="center">
                        <Icon as={BiText} mr={2} boxSize="14px" />
                        Text Styling
                      </Flex>
                    </Box>
                    <AccordionIcon />
                  </AccordionButton>
                </h2>
                <AccordionPanel pb={2} pt={1}>
                  <Box mb={3}>
                    <Text fontSize="sm" fontWeight="medium" mb={1} color={labelColor}>Axes Labels</Text>
                    <Grid templateColumns="auto 1fr" gap={2} alignItems="center" ml={2}>
                      <FormLabel fontSize="sm" mb={0} whiteSpace="nowrap" color={labelColor}>Font Size:</FormLabel>
                      <Input 
                        type="number"
                        value={axisTextFontSize} 
                        onChange={(e) => {
                          const value = e.target.value;
                          // Allow empty, dot, or valid number
                          if (value === "" || value === "." || !isNaN(parseFloat(value))) {
                            setAxisTextFontSize(value);
                          }
                        }}
                        size="sm"
                        h="32px"
                        bg="gray.700"
                        color={editableFieldColor}
                      />
                      
                      <FormLabel fontSize="sm" mb={0} whiteSpace="nowrap" color={labelColor}>Font Family:</FormLabel>
                      <Select 
                        size="sm" 
                        h="32px"
                        value={axisTextFontFamily} 
                        onChange={(e) => setAxisTextFontFamily(e.target.value)}
                        bg="gray.700"
                        color={editableFieldColor}
                        sx={{
                          option: {
                            bg: "gray.700",
                            _hover: { bg: "gray.600" }
                          }
                        }}
                      >
                        <option value="Arial">Arial</option>
                        <option value="Helvetica">Helvetica</option>
                        <option value="Times New Roman">Times New Roman</option>
                        <option value="Courier New">Courier New</option>
                        <option value="Verdana">Verdana</option>
                        <option value="Georgia">Georgia</option>
                      </Select>
                      
                      <FormLabel fontSize="sm" mb={0} whiteSpace="nowrap" color={labelColor}>Font Style:</FormLabel>
                      <Select 
                        size="sm" 
                        value={axisTextFontStyle} 
                        onChange={(e) => setAxisTextFontStyle(e.target.value)}
                        bg="gray.700"
                        color={editableFieldColor}
                        sx={{
                          option: {
                            bg: "gray.700",
                            _hover: { bg: "gray.600" }
                          }
                        }}
                      >
                        <option value="normal">Normal</option>
                        <option value="bold">Bold</option>
                        <option value="italic">Italic</option>
                        <option value="bold italic">Bold Italic</option>
                      </Select>
                    </Grid>
                  </Box>
                  
                  <Box>
                    <Text fontSize="sm" fontWeight="medium" mb={1} color={labelColor}>Slope & Intercept</Text>
                    <Grid templateColumns="auto 1fr" gap={2} alignItems="center" ml={2}>
                      <FormLabel fontSize="sm" mb={0} whiteSpace="nowrap" color={labelColor}>Font Size:</FormLabel>
                      <Input 
                        type="number"
                        value={slopeTextFontSize} 
                        onChange={(e) => {
                          const value = e.target.value;
                          // Allow empty, dot, or valid number
                          if (value === "" || value === "." || !isNaN(parseFloat(value))) {
                            setSlopeTextFontSize(value);
                          }
                        }}
                        size="sm"
                        h="32px"
                        bg="gray.700"
                        color={editableFieldColor}
                      />
                      
                      <FormLabel fontSize="sm" mb={0} whiteSpace="nowrap" color={labelColor}>Font Family:</FormLabel>
                      <Select 
                        size="sm" 
                        value={slopeTextFontFamily} 
                        onChange={(e) => setSlopeTextFontFamily(e.target.value)}
                        bg="gray.700"
                        color={editableFieldColor}
                        sx={{
                          option: {
                            bg: "gray.700",
                            _hover: { bg: "gray.600" }
                          }
                        }}
                      >
                        <option value="Arial">Arial</option>
                        <option value="Helvetica">Helvetica</option>
                        <option value="Times New Roman">Times New Roman</option>
                        <option value="Courier New">Courier New</option>
                        <option value="Verdana">Verdana</option>
                        <option value="Georgia">Georgia</option>
                      </Select>
                      
                      <FormLabel fontSize="sm" mb={0} whiteSpace="nowrap" color={labelColor}>Font Style:</FormLabel>
                      <Select 
                        size="sm" 
                        value={slopeTextFontStyle} 
                        onChange={(e) => setSlopeTextFontStyle(e.target.value)}
                        bg="gray.700"
                        color={editableFieldColor}
                        sx={{
                          option: {
                            bg: "gray.700",
                            _hover: { bg: "gray.600" }
                          }
                        }}
                      >
                        <option value="normal">Normal</option>
                        <option value="bold">Bold</option>
                        <option value="italic">Italic</option>
                        <option value="bold italic">Bold Italic</option>
                      </Select>
                    </Grid>
                  </Box>
                </AccordionPanel>
              </AccordionItem>
            </Accordion>

            {/* Fixed-width table layout to prevent overflow */}
            <Box 
              borderWidth="1px" 
              p={2} 
              rounded="md" 
              bg={tableBg} 
              mb={4} 
              overflowX="hidden" 
              borderColor="blue.600"
              width="100%"
              shadow="sm"
            >
              <Box width="100%">
                <Table variant="simple" size="sm" layout="fixed" sx={{ borderSpacing: 0, borderCollapse: "collapse" }} width="100%">
                  <Thead bg={tableHeaderBg}>
                    <Tr>
                      <Th w="35px" textAlign="center" fontSize="xs" py={1}>#</Th>
                      <Th w="85px" sx={{textTransform: 'none', fontSize: 'md'}} textAlign="center" py={1} fontWeight="bold">σ₃</Th>
                      <Th w="85px" sx={{textTransform: 'none', fontSize: 'md'}} textAlign="center" py={1} fontWeight="bold">σ₁</Th>
                      <Th w="92px" textAlign="center" fontSize="xs" py={1}>CENTER</Th>
                      <Th w="92px" textAlign="center" fontSize="xs" py={1}>RADIUS</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {inputs.map((row, i) => (
                      <Tr key={i}>
                        <Td textAlign="center" color="white" fontWeight="medium" p="4px 0" fontSize="xs">{i + 1}</Td>
                        <Td p="4px 1px">
                          <Input 
                            size="sm" 
                            value={row.sigma3} 
                            onChange={e => handleInputChange(i, "sigma3", e.target.value)} 
                            w="full"
                            bg="gray.700"
                            color={editableFieldColor}
                            borderColor="gray.600"
                            _hover={{ borderColor: "blue.400" }}
                            _focus={{ borderColor: "blue.400", boxShadow: "0 0 0 1px blue.400" }}
                            fontSize="sm"
                            fontWeight="bold"
                            textAlign="center"
                            px={1}
                            h="32px"
                            className="sigma3-input editable-field"
                          />
                        </Td>
                        <Td p="4px 1px">
                          <Input 
                            size="sm" 
                            value={row.sigma1} 
                            onChange={e => handleInputChange(i, "sigma1", e.target.value)}
                            w="full" 
                            bg="gray.700"
                            color={editableFieldColor}
                            borderColor="gray.600"
                            _hover={{ borderColor: "blue.400" }}
                            _focus={{ borderColor: "blue.400", boxShadow: "0 0 0 1px blue.400" }}
                            fontSize="sm"
                            fontWeight="bold"
                            textAlign="center"
                            px={1}
                            h="32px"
                            className="sigma1-input editable-field"
                          />
                        </Td>
                        <Td p="4px 1px">
                          <Input 
                            size="sm" 
                            value={row.center} 
                            isReadOnly 
                            bg={inputReadOnlyBg}
                            color={editableFieldColor}
                            w="full" 
                            borderColor="gray.600"
                            fontSize="xs"
                            cursor="not-allowed"
                            _hover={{ borderColor: "red.500" }}
                            opacity={0.85}
                            fontWeight="medium"
                            textAlign="center"
                            px={1}
                            h="28px"
                            className="read-only-field"
                          />
                        </Td>
                        <Td p="4px 1px">
                          <Input 
                            size="sm" 
                            value={row.radius} 
                            isReadOnly 
                            bg={inputReadOnlyBg}
                            color={editableFieldColor}
                            w="full" 
                            borderColor="gray.600"
                            fontSize="xs"
                            cursor="not-allowed"
                            _hover={{ borderColor: "red.500" }}
                            opacity={0.85}
                            fontWeight="medium"
                            textAlign="center"
                            px={1}
                            h="28px"
                            className="read-only-field"
                          />
                        </Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </Box>
            </Box>

            <Flex direction="column" gap={2}>
              <Button 
                colorScheme="blue" 
                onClick={calculateCircles} 
                h="34px"
                _hover={{ bg: "blue.500" }}
                _active={{ bg: "blue.600" }}
                leftIcon={<Icon as={BiReset} boxSize="16px" />}
                fontSize="sm"
              >
                Draw/Redraw Mohr Circles and Mohr Failure Envelope
              </Button>
              
              <Box height="1px" bg="gray.700" my="1px" />
              
              <Button 
                colorScheme="green" 
                onClick={calculateLineEquation} 
                isDisabled={!linePoints} 
                h="34px"
                _hover={{ bg: "green.500" }}
                _active={{ bg: "green.600" }}
                opacity={!linePoints ? 0.6 : 1}
                leftIcon={<Icon as={BiCalculator} boxSize="16px" />}
                fontSize="sm"
              >
                Get Tangent Slope & Intercept
              </Button>
              
              <Box height="1px" bg="gray.700" my="1px" />
              
              <Button 
                colorScheme="purple" 
                onClick={exportImage} 
                isDisabled={!linePoints} 
                h="34px"
                _hover={{ bg: "purple.500" }}
                _active={{ bg: "purple.600" }}
                opacity={!linePoints ? 0.6 : 1}
                leftIcon={<Icon as={BiDownload} boxSize="16px" />}
                fontSize="sm"
              >
                Export Canvas as JPG
              </Button>
            </Flex>

            {slope && (
              <Box 
                p={3} 
                bg={resultsBg} 
                rounded="md" 
                fontWeight="medium" 
                mt={3}
                borderWidth="1px"
                borderColor="blue.500"
                className="mohr-result"
                fontSize="sm"
              >
                <Text color="white">Slope (m): <strong>{slope}</strong></Text>
                <Text color="white">Intercept (c): <strong>{intercept}</strong></Text>
              </Box>
            )}
          </Box>
        </GridItem>

        {/* Right Column - Canvas */}
        <GridItem>
          <Box bg={cardBg} p={3} rounded="md" shadow="lg" borderWidth="1px" borderColor={borderColor} maxW="100%" overflow="hidden">
            <Text fontSize="sm" fontWeight="semibold" mb={3} color={uiTextColor}>Visualization</Text>
            
            <Box w="100%" overflow="hidden" position="relative" className="mohr-canvas-container">
              {/* Canvas - scales automatically to fit container without scrollbars */}
              <Stage width={canvasSize.width} height={canvasSize.height} ref={stageRef} style={{ background: bgColor, borderRadius: 6, display: "block", maxWidth: "100%" }} className="mohr-canvas">
                <Layer>
                  {/* Clear background explicitly to ensure consistent color */}
                  <Rect x={0} y={0} width={canvasSize.width} height={canvasSize.height} fill={bgColor} />
                  {drawAxes()}
                  {circles.map((c, i) => (
                    <Path 
                      key={i} 
                      data={generateArcPath(
                        (c.center - canvasSize.xMin) * baseScale * canvasSize.appliedScale, 
                        canvasSize.height - (-canvasSize.yMin) * baseScale * canvasSize.appliedScale, 
                        c.radius * baseScale * canvasSize.appliedScale
                      )} 
                      stroke={circleColors[i] || "#2b6cb0"} 
                      strokeWidth={circleThickness[i] === "" || circleThickness[i] === "." ? 2 : Number(circleThickness[i])} 
                    />
                  ))}
                  {linePoints && (
                    <>
                      <Line 
                        points={[linePoints.x1, linePoints.y1, linePoints.x2, linePoints.y2]} 
                        stroke={tangentColor} 
                        strokeWidth={tangentThickness === "" || tangentThickness === "." ? 2 : Number(tangentThickness)} 
                      />
                      <KonvaText 
                        text={`Slope (m): ${slope ?? "-"} | Intercept (c): ${intercept ?? "-"}`} 
                        x={canvasSize.width / 2} 
                        y={(canvasSize.height - (0 - canvasSize.yMin) * baseScale * canvasSize.appliedScale) + 30} 
                        offsetX={140} 
                        fill={textColor} 
                        fontSize={slopeTextFontSize === "" || slopeTextFontSize === "." ? 16 : Number(slopeTextFontSize)}
                        fontFamily={slopeTextFontFamily}
                        fontStyle={slopeTextFontStyle}
                      />
                      <KonvaCircle 
                        x={linePoints.x1} 
                        y={linePoints.y1} 
                        radius={10} 
                        fill="#fff" 
                        stroke={tangentColor} 
                        strokeWidth={tangentThickness === "" || tangentThickness === "." ? 2 : Number(tangentThickness)} 
                        draggable 
                        dragBoundFunc={pos => ({ x: linePoints.x1, y: pos.y })} 
                        onDragMove={e => handleDrag("y1", e.target.absolutePosition())}
                        onMouseEnter={e => {
                          const stage = e.target.getStage();
                          document.body.style.cursor = 'pointer';
                          // Add a simple text tooltip without trying to construct a new KonvaText
                          const layer = stage.getLayers()[0];
                          // Create tooltip text using the existing layer and draw method
                          const tooltipText = new window.Konva.Text({
                            text: 'Drag up/down to adjust tangent line',
                            fontSize: 12,
                            fontFamily: 'Arial',
                            fill: textColor,
                            padding: 5,
                            x: e.target.x() + 15,
                            y: e.target.y() - 25,
                          });
                          // Add background rectangle
                          const textWidth = tooltipText.width();
                          const textHeight = tooltipText.height();
                          const tooltipBg = new window.Konva.Rect({
                            x: e.target.x() + 15 - 5,
                            y: e.target.y() - 25 - 5,
                            width: textWidth + 10,
                            height: textHeight + 10,
                            fill: bgColor,
                            cornerRadius: 5,
                          });
                          // Add to layer in correct order
                          layer.add(tooltipBg);
                          layer.add(tooltipText);
                          layer.draw();
                          // Store references to clean up
                          e.target.tooltipText = tooltipText;
                          e.target.tooltipBg = tooltipBg;
                        }}
                        onMouseLeave={e => {
                          document.body.style.cursor = 'default';
                          // Remove tooltip
                          if (e.target.tooltipText) {
                            e.target.tooltipText.destroy();
                            e.target.tooltipBg.destroy();
                            e.target.getStage().getLayers()[0].draw();
                          }
                        }}
                      />
                      <KonvaCircle 
                        x={linePoints.x2} 
                        y={linePoints.y2} 
                        radius={10} 
                        fill="#fff" 
                        stroke={tangentColor} 
                        strokeWidth={tangentThickness === "" || tangentThickness === "." ? 2 : Number(tangentThickness)} 
                        draggable 
                        dragBoundFunc={pos => ({ x: linePoints.x2, y: pos.y })} 
                        onDragMove={e => handleDrag("y2", e.target.absolutePosition())}
                        onMouseEnter={e => {
                          const stage = e.target.getStage();
                          document.body.style.cursor = 'pointer';
                          // Add a simple text tooltip without trying to construct a new KonvaText
                          const layer = stage.getLayers()[0];
                          // Create tooltip text using the existing layer and draw method
                          const tooltipText = new window.Konva.Text({
                            text: 'Drag up/down to adjust tangent line',
                            fontSize: 12,
                            fontFamily: 'Arial',
                            fill: textColor,
                            padding: 5,
                            x: e.target.x() + 15,
                            y: e.target.y() - 25,
                          });
                          // Add background rectangle
                          const textWidth = tooltipText.width();
                          const textHeight = tooltipText.height();
                          const tooltipBg = new window.Konva.Rect({
                            x: e.target.x() + 15 - 5,
                            y: e.target.y() - 25 - 5,
                            width: textWidth + 10,
                            height: textHeight + 10,
                            fill: bgColor,
                            cornerRadius: 5,
                          });
                          // Add to layer in correct order
                          layer.add(tooltipBg);
                          layer.add(tooltipText);
                          layer.draw();
                          // Store references to clean up
                          e.target.tooltipText = tooltipText;
                          e.target.tooltipBg = tooltipBg;
                        }}
                        onMouseLeave={e => {
                          document.body.style.cursor = 'default';
                          // Remove tooltip
                          if (e.target.tooltipText) {
                            e.target.tooltipText.destroy();
                            e.target.tooltipBg.destroy();
                            e.target.getStage().getLayers()[0].draw();
                          }
                        }}
                      />
                    </>
                  )}
                </Layer>
              </Stage>
            </Box>
          </Box>
        </GridItem>
      </Grid>
    </Box>
  );
};

export default App;
